import { PolfEventRankingEnum } from '@polf/shared/codegen/types'
import { useState } from 'react'

import type { LeaderboardUnionFieldsFragment } from '../../__generated__/hooks'
import Header, { scoringNote } from './Header'
import Hero from './Hero'
import type { CourseHoleSummary } from './MiniScorecard'
import TeamDetail from './TeamDetail'
import TeamRow from './TeamRow'
import Toggle from './Toggle'

type SegmentedLb = Extract<
  LeaderboardUnionFieldsFragment,
  { __typename: 'SegmentedLeaderboard' }
>
/**
 * The segment boards and the overall board carry the same entry shape except
 * for segmentTotals, which only the overall one has — a segment has nothing to
 * break down. Rows are normalised to the common shape with the totals carried
 * alongside, so the list renders once.
 */
type SegmentedTeam = SegmentedLb['segments'][number]['teams'][number]
type SegmentTotals = NonNullable<SegmentedLb['teams'][number]['segmentTotals']>

/**
 * Hole-scores a block asks a team for.
 *
 * One per hole wherever the side plays a single ball — the one-ball formats
 * outright, and best ball, which counts the better of the two. A singles block
 * is played 1v1 and 1v1, so BOTH partners' balls count and the block asks for
 * one per member per hole. Over 6+6+6 a pair therefore records 24 across 18
 * holes, and 24/24 is a complete card.
 */
const expectedHoleScores = (
  segment: { fromHole: number; toHole: number; format: string },
  memberCount: number
): number => {
  const holes = segment.toHole - segment.fromHole + 1
  return segment.format === 'GOLF' ? holes * memberCount : holes
}

const FORMAT_LABEL: Record<string, string> = {
  SCRAMBLE: 'Scramble',
  BESTBALL: 'Best ball',
  FOURSOME: 'Foursome',
  GOLF: 'Singel',
}

/**
 * Four boards behind one toggle: the round total, then one per segment.
 *
 * One at a time rather than stacked, because a phone that shows four boards
 * shows none of them. Totalt leads because it is the competition; the segment
 * boards are how it was arrived at.
 *
 * The overall row carries its three parts rather than one number. A split
 * round's total is NOT comparable to an ordinary round — the singles segment
 * counts both partners, so the team records 24 hole-scores over 18 holes — and
 * a bare "89" reads as a golf score to everyone who sees it.
 */
const SegmentedView = ({
  lb,
  yourTeamId,
  ranking,
  courseHoles,
}: {
  lb: SegmentedLb
  yourTeamId?: string | null
  ranking?: PolfEventRankingEnum | null
  courseHoles: readonly CourseHoleSummary[]
}) => {
  const isNet = ranking === PolfEventRankingEnum.NetStrokes
  const [tab, setTab] = useState<string>('total')
  const [selected, setSelected] = useState<SegmentedTeam | null>(null)

  const segments = lb.segments ?? []
  const options = [
    { value: 'total', label: 'Totalt' },
    ...segments.map((s) => ({
      value: String(s.ordinal),
      label: `${s.fromHole}–${s.toHole}`,
    })),
  ]

  const active = segments.find((s) => String(s.ordinal) === tab) ?? null
  const expectedFor = (entry: SegmentedTeam) => {
    const members = entry.team.players?.length ?? 2
    const counted = active ? [active] : segments
    return counted.reduce(
      (sum, segment) => sum + expectedHoleScores(segment, members),
      0
    )
  }
  const rows: {
    entry: SegmentedTeam
    totals: SegmentTotals | null
    expected: number
  }[] = active
    ? (active.teams ?? []).map((entry) => ({
        entry,
        totals: null,
        expected: expectedFor(entry),
      }))
    : (lb.teams ?? []).map((entry) => ({
        entry,
        totals: entry.segmentTotals ?? null,
        expected: expectedFor(entry),
      }))
  const yourTeam = yourTeamId
    ? rows.find((r) => r.entry.team.id === yourTeamId)?.entry ?? null
    : null

  return (
    <section className="pb-4">
      <Header scoring={scoringNote(ranking)} />

      <div className="flex justify-center px-4 pb-2">
        <Toggle options={options} value={tab} onChange={setTab} />
      </div>

      {active ? (
        <p className="px-4 pb-2 text-center text-xs text-ink-500">
          {FORMAT_LABEL[active.format] ?? active.format} · hull{' '}
          {active.fromHole}–{active.toHole}
        </p>
      ) : (
        <p className="px-4 pb-2 text-center text-xs text-ink-500">
          Summen av de tre etappene. Singel teller begge makkerne, så et
          fullført kort er 24 scorer på 18 hull.
        </p>
      )}

      {yourTeam ? (
        <Hero
          yourRank={yourTeam.rank ?? null}
          yourPrimary={String(isNet ? yourTeam.strokesNet : yourTeam.points)}
          primarySuffix={isNet ? 'netto' : 'pts'}
          leaderGap={null}
          totalPlayers={rows.length}
          unit="lag"
        />
      ) : null}

      <ul className="bg-paper mx-2 rounded-lg shadow-sm overflow-hidden">
        {rows.map(({ entry, totals, expected }) => (
          <TeamRow
            key={entry.team.id}
            entry={entry}
            holesTotal={expected}
            isYou={yourTeamId != null && entry.team.id === yourTeamId}
            isNet={isNet}
            netAs="strokes"
            onClick={() => setSelected(entry)}
            footer={
              totals?.length ? (
                // Named, not a bare sum: "20 + 12 + 0 = 32" says nothing about
                // what the parts are, and the total is 24 hole-scores rather
                // than a round, so it has to carry its own explanation. Indented
                // past the rank and avatar so it reads as this row's detail.
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 pb-2 pl-[4.25rem] pr-4 text-[11px] text-ink-500">
                  {totals.map((t) => (
                    <span key={t.ordinal}>
                      {FORMAT_LABEL[t.format] ?? t.format}{' '}
                      <span className="font-mono tabular-nums text-ink-700">
                        {isNet ? t.strokesNet : t.points}
                      </span>
                    </span>
                  ))}
                </div>
              ) : null
            }
          />
        ))}
      </ul>

      <TeamDetail
        open={selected != null}
        onClose={() => setSelected(null)}
        courseHoles={courseHoles}
        data={
          selected
            ? {
                rank: selected.rank ?? null,
                teamName: selected.team.name ?? 'Uten navn',
                flightNumber: selected.team.flight?.flightNumber ?? null,
                members: selected.team.players ?? [],
                score: selected.score,
                strokes: selected.strokes,
                strokesNet: selected.strokesNet,
                points: selected.points,
                holesRegistred: selected.holesRegistred,
                breakdown: rows
                  .find((r) => r.entry.team.id === selected.team.id)
                  ?.totals?.map((t) => {
                    const seg = segments.find((x) => x.ordinal === t.ordinal)
                    return {
                      ordinal: t.ordinal,
                      label: FORMAT_LABEL[t.format] ?? t.format,
                      holes: seg ? `${seg.fromHole}–${seg.toHole}` : null,
                      value: isNet ? t.strokesNet : t.points,
                    }
                  }),
                holesTotal: rows.find(
                  (r) => r.entry.team.id === selected.team.id
                )?.expected,
                scores: selected.scores,
              }
            : null
        }
      />
    </section>
  )
}

export default SegmentedView
